import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Switch from "@material-ui/core/Switch";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TablePagination from "@material-ui/core/TablePagination";
import MenuItem from "@material-ui/core/MenuItem";
import Chip from "@material-ui/core/Chip";
import Avatar from "@material-ui/core/Avatar";
import Autocomplete from "@material-ui/lab/Autocomplete";
import RefreshIcon from "@material-ui/icons/Refresh";
import SaveIcon from "@material-ui/icons/Save";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import api from "../../services/api";
import openSocket from "../../services/socket-io";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { usePrivacyCensorSettings } from "../../utils/privacyCensor";

const NO_MONITORING_CONNECTION = "__no_monitoring_connection__";
const NO_MONITORING_ACCOUNT = "__no_monitoring_account__";
const NO_FIRST_CONNECTION_ACCOUNT = "__no_first_connection_account__";
const NO_FIRST_CONNECTION_DESTINATION = "__no_first_connection_destination__";
const NO_DISCONNECTED_SENDER_ACCOUNT = "__no_disconnected_sender_account__";
const NO_DISCONNECTED_SENDER_CONNECTION = "__no_disconnected_sender_connection__";
const NO_EXPIRATION_SENDER_ACCOUNT = "__no_expiration_sender_account__";
const NO_EXPIRATION_SENDER_CONNECTION = "__no_expiration_sender_connection__";

const useStyles = makeStyles(theme => ({
  mainPaper: {
    flex: 1,
    margin: 20,
    padding: theme.spacing(2),
    overflowY: "auto",
    ...theme.scrollbarStyles
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr"
    }
  },
  metric: {
    padding: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6
  },
  section: {
    marginTop: theme.spacing(2)
  },
  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  settingsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: theme.spacing(1.5),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr"
    }
  },
  fullWidth: {
    gridColumn: "1 / -1"
  },
  delayField: {
    width: 90
  },
  tableWrap: {
    overflowX: "auto"
  },
  muted: {
    color: theme.palette.text.secondary
  },
  censoredText: {
    filter: "blur(4px)",
    userSelect: "none"
  },
  censoredPhoto: {
    filter: "blur(6px)"
  }
}));

const formatDate = value => {
  if (!value) return "-";
  return new Date(value).toLocaleString();
};

const formatDuration = seconds => {
  const value = Number(seconds || 0);
  const minutes = Math.floor(value / 60);
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (hours) return `${hours}h ${remainingMinutes}m`;
  if (minutes) return `${minutes}m`;
  return `${value}s`;
};

const formatProfile = profile => {
  if (profile === "owner") return "Dueño";
  if (profile === "superadmin") return "Superadministrador";
  return profile || "-";
};

const Monitoring = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const privacyCensorSettings = usePrivacyCensorSettings(user?.id);
  const [loading, setLoading] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [settings, setSettings] = useState({
    enabled: false,
    senderCompanyId: "",
    contactId: "",
    connectionId: "",
    message: "",
    delaySeconds: 0
  });
  const [firstConnectionSettings, setFirstConnectionSettings] = useState({
    enabled: false,
    destinationCompanyId: "",
    destinationConnectionId: "",
    message: "",
    delaySeconds: 0
  });
  const [disconnectedConnectionSettings, setDisconnectedConnectionSettings] = useState({
    enabled: false,
    senderCompanyId: "",
    senderConnectionId: "",
    message: "",
    delaySeconds: 0
  });
  const [expirationAutomationSettings, setExpirationAutomationSettings] = useState({
    enabled: false,
    senderCompanyId: "",
    senderConnectionId: "",
    deleteEnabled: false,
    deleteAfterDays: 7,
    day3Message: "",
    day2Message: "",
    day1Message: ""
  });
  const [mediaFile, setMediaFile] = useState(null);
  const [clearMedia, setClearMedia] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [contactSearch, setContactSearch] = useState("");
  const [contactAutocompleteOpen, setContactAutocompleteOpen] = useState(false);
  const [notificationAccounts, setNotificationAccounts] = useState([]);

  const selectedContact =
    contacts.find(contact => Number(contact.id) === Number(settings.contactId)) || null;
  const hasSelectedContact = Boolean(selectedContact?.id);
  const loginSenderAccount =
    notificationAccounts.find(
      account => Number(account.id) === Number(settings.senderCompanyId)
    ) || null;
  const loginSenderConnections =
    loginSenderAccount?.connections?.filter(
      connection => connection.channel === "whatsapp"
    ) || [];
  const firstConnectionAccount =
    notificationAccounts.find(
      account =>
        Number(account.id) === Number(firstConnectionSettings.destinationCompanyId)
    ) || null;
  const firstConnectionDestinations =
    firstConnectionAccount?.connections?.filter(
      connection => connection.channel === "whatsapp"
    ) || [];
  const disconnectedSenderAccount =
    notificationAccounts.find(
      account =>
        Number(account.id) ===
        Number(disconnectedConnectionSettings.senderCompanyId)
    ) || null;
  const disconnectedSenderConnections =
    disconnectedSenderAccount?.connections?.filter(
      connection => connection.channel === "whatsapp"
    ) || [];
  const expirationSenderAccount =
    notificationAccounts.find(
      account =>
        Number(account.id) === Number(expirationAutomationSettings.senderCompanyId)
    ) || null;
  const expirationSenderConnections =
    expirationSenderAccount?.connections?.filter(
      connection => connection.channel === "whatsapp"
    ) || [];
  const contactOptions = useMemo(() => {
    const options = [...contacts];
    if (
      selectedContact?.id &&
      !options.some(option => String(option.id) === String(selectedContact.id))
    ) {
      options.unshift(selectedContact);
    }
    return options;
  }, [contacts, selectedContact]);

  const renderContactOption = option => (
    <>
      <Avatar
        src={privacyCensorSettings.contactPhotos ? undefined : option.profilePicUrl}
        className={privacyCensorSettings.contactPhotos ? classes.censoredPhoto : undefined}
        style={{ marginRight: 8 }}
      />
      <div>
        <Typography
          variant="body2"
          className={privacyCensorSettings.contactNames ? classes.censoredText : undefined}
        >
          {privacyCensorSettings.contactNames ? "Contacto privado" : option.name || "-"}
        </Typography>
        <Typography
          variant="caption"
          color="textSecondary"
          className={privacyCensorSettings.contactNames ? classes.censoredText : undefined}
        >
          {privacyCensorSettings.contactNames ? "Telefono privado" : option.number || ""}
        </Typography>
      </div>
    </>
  );

  const fetchMonitoring = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/monitoring", {
        params: {
          pageNumber: page + 1,
          rowsPerPage
        }
      });

      setOnlineUsers(data.onlineUsers || []);
      setLogs(data.logs || []);
      setCount(data.count || 0);
      setSettings(current => ({
        ...current,
        ...(data.notificationSettings || {})
      }));
      setFirstConnectionSettings(current => ({
        ...current,
        ...(data.firstConnectionSettings || {})
      }));
      setDisconnectedConnectionSettings(current => ({
        ...current,
        ...(data.disconnectedConnectionSettings || {})
      }));
      setExpirationAutomationSettings(current => ({
        ...current,
        ...(data.expirationAutomationSettings || {})
      }));
      setNotificationAccounts(data.notificationAccounts || []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage]);

  useEffect(() => {
    fetchMonitoring();
  }, [fetchMonitoring]);

  useEffect(() => {
    const normalizedSearch = contactSearch.trim();
    if (!settings.senderCompanyId) return undefined;
    if (normalizedSearch.length < 2 && !contactAutocompleteOpen) return undefined;

    const delay = setTimeout(async () => {
      try {
        const { data } = await api.get("/monitoring/contacts", {
          params: {
            companyId: settings.senderCompanyId,
            searchParam: normalizedSearch,
            pageNumber: 1
          }
        });
        setContacts(currentContacts => {
          const nextContacts = data.contacts || [];
          const currentSelected = settings.contactId
            ? currentContacts.find(
                contact => Number(contact.id) === Number(settings.contactId)
              )
            : null;

          if (
            currentSelected?.id &&
            !nextContacts.some(
              contact => Number(contact.id) === Number(currentSelected.id)
            )
          ) {
            return [currentSelected, ...nextContacts];
          }

          return nextContacts;
        });
      } catch (err) {
        toastError(err);
      }
    }, 350);

    return () => clearTimeout(delay);
  }, [
    contactSearch,
    contactAutocompleteOpen,
    settings.senderCompanyId,
    settings.contactId
  ]);

  useEffect(() => {
    if (!settings.contactId) return undefined;
    if (hasSelectedContact) return undefined;

    let active = true;

    const loadSelectedContact = async () => {
      try {
        const { data } = await api.get(`/monitoring/contacts/${settings.contactId}`, {
          params: { companyId: settings.senderCompanyId || "" }
        });
        if (!active || !data?.id) return;
        setContacts(currentContacts =>
          currentContacts.some(contact => Number(contact.id) === Number(data.id))
            ? currentContacts
            : [data, ...currentContacts]
        );
      } catch (err) {
        toastError(err);
      }
    };

    loadSelectedContact();

    return () => {
      active = false;
    };
  }, [settings.contactId, settings.senderCompanyId, hasSelectedContact]);

  useEffect(() => {
    const socket = openSocket();

    socket.on("monitoring", () => {
      fetchMonitoring();
    });

    return () => socket.disconnect();
  }, [fetchMonitoring]);

  const handleSaveSettings = async () => {
    try {
      const formData = new FormData();
      formData.append("enabled", settings.enabled ? "true" : "false");
      formData.append("senderCompanyId", settings.senderCompanyId || "");
      formData.append("contactId", settings.contactId || "");
      formData.append("connectionId", settings.connectionId || "");
      formData.append("message", settings.message || "");
      formData.append("delaySeconds", settings.delaySeconds || 0);
      formData.append("clearMedia", clearMedia ? "true" : "false");
      if (mediaFile) formData.append("media", mediaFile);

      const { data } = await api.put("/monitoring/notification-settings", formData);
      setSettings(data);
      setMediaFile(null);
      setClearMedia(false);
      toast.success("Configuracion guardada.");
    } catch (err) {
      toastError(err);
    }
  };

  const handleSaveFirstConnectionSettings = async () => {
    try {
      const { data } = await api.put("/monitoring/first-connection-settings", {
        enabled: firstConnectionSettings.enabled,
        destinationCompanyId:
          firstConnectionSettings.destinationCompanyId || "",
        destinationConnectionId:
          firstConnectionSettings.destinationConnectionId || "",
        message: firstConnectionSettings.message || "",
        delaySeconds: firstConnectionSettings.delaySeconds || 0
      });
      setFirstConnectionSettings(data);
      toast.success("Configuracion guardada.");
    } catch (err) {
      toastError(err);
    }
  };

  const handleSaveDisconnectedConnectionSettings = async () => {
    try {
      const { data } = await api.put(
        "/monitoring/disconnected-connection-settings",
        {
          enabled: disconnectedConnectionSettings.enabled,
          senderCompanyId:
            disconnectedConnectionSettings.senderCompanyId || "",
          senderConnectionId:
            disconnectedConnectionSettings.senderConnectionId || "",
          message: disconnectedConnectionSettings.message || "",
          delaySeconds: disconnectedConnectionSettings.delaySeconds || 0
        }
      );
      setDisconnectedConnectionSettings(data);
      toast.success("Configuracion guardada.");
    } catch (err) {
      toastError(err);
    }
  };

  const handleSaveExpirationAutomationSettings = async () => {
    try {
      const { data } = await api.put(
        "/monitoring/expiration-automation-settings",
        {
          enabled: expirationAutomationSettings.enabled,
          senderCompanyId: expirationAutomationSettings.senderCompanyId || "",
          senderConnectionId:
            expirationAutomationSettings.senderConnectionId || "",
          deleteEnabled: expirationAutomationSettings.deleteEnabled,
          deleteAfterDays: expirationAutomationSettings.deleteAfterDays || 0,
          day3Message: expirationAutomationSettings.day3Message || "",
          day2Message: expirationAutomationSettings.day2Message || "",
          day1Message: expirationAutomationSettings.day1Message || ""
        }
      );
      setExpirationAutomationSettings(data);
      toast.success("Configuracion guardada.");
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>Monitoring</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="outlined"
            color="primary"
            onClick={fetchMonitoring}
            startIcon={<RefreshIcon />}
            disabled={loading}
          >
            Actualizar
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper}>
        <div className={classes.grid}>
          <div className={classes.metric}>
            <Typography variant="caption" className={classes.muted}>
              Usuarios online
            </Typography>
            <Typography variant="h5">{onlineUsers.length}</Typography>
          </div>
          <div className={classes.metric}>
            <Typography variant="caption" className={classes.muted}>
              Sesiones registradas
            </Typography>
            <Typography variant="h5">{count}</Typography>
          </div>
          <div className={classes.metric}>
            <Typography variant="caption" className={classes.muted}>
              Notificacion de login
            </Typography>
            <Typography variant="h5">{settings.enabled ? "Activa" : "Inactiva"}</Typography>
          </div>
          <div className={classes.metric}>
            <Typography variant="caption" className={classes.muted}>
              Primera conexion
            </Typography>
            <Typography variant="h5">
              {firstConnectionSettings.enabled ? "Activa" : "Inactiva"}
            </Typography>
          </div>
          <div className={classes.metric}>
            <Typography variant="caption" className={classes.muted}>
              Conexion desvinculada
            </Typography>
            <Typography variant="h5">
              {disconnectedConnectionSettings.enabled ? "Activa" : "Inactiva"}
            </Typography>
          </div>
          <div className={classes.metric}>
            <Typography variant="caption" className={classes.muted}>
              Expiracion de cuentas
            </Typography>
            <Typography variant="h5">
              {expirationAutomationSettings.enabled ||
              expirationAutomationSettings.deleteEnabled
                ? "Activa"
                : "Inactiva"}
            </Typography>
          </div>
        </div>

        <div className={classes.section}>
          <div className={classes.sectionHeader}>
            <Typography variant="h6">Notificacion de inicios de sesion</Typography>
            <Button
              color="primary"
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSaveSettings}
            >
              Guardar
            </Button>
          </div>
          <div className={classes.settingsGrid}>
            <FormControlLabel
              control={
                <Switch
                  color="primary"
                  checked={Boolean(settings.enabled)}
                  onChange={event =>
                    setSettings({ ...settings, enabled: event.target.checked })
                  }
                />
              }
              label="Notificar cuando un usuario inicia sesion"
            />
            <TextField
              select
              label="Cuenta emisora"
              variant="outlined"
              margin="dense"
              value={settings.senderCompanyId || NO_MONITORING_ACCOUNT}
              onChange={event => {
                setSettings({
                  ...settings,
                  senderCompanyId:
                    event.target.value === NO_MONITORING_ACCOUNT
                      ? ""
                      : event.target.value,
                  connectionId: "",
                  contactId: ""
                });
                setContacts([]);
                setContactSearch("");
              }}
            >
              <MenuItem value={NO_MONITORING_ACCOUNT}>Seleccionar</MenuItem>
              {notificationAccounts.map(account => (
                <MenuItem key={account.id} value={account.id}>
                  {account.name || `Cuenta ${account.id}`}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Conexion para enviar"
              variant="outlined"
              margin="dense"
              value={settings.connectionId || NO_MONITORING_CONNECTION}
              onChange={event =>
                setSettings({
                  ...settings,
                  connectionId:
                    event.target.value === NO_MONITORING_CONNECTION
                      ? ""
                      : event.target.value
                })
              }
              disabled={!settings.senderCompanyId}
            >
              <MenuItem value={NO_MONITORING_CONNECTION}>Seleccionar</MenuItem>
              {loginSenderConnections.map(connection => (
                <MenuItem key={connection.id} value={connection.id}>
                  {connection.name} ({connection.number || "sin numero"}) -{" "}
                  {connection.status || "sin estado"}
                </MenuItem>
              ))}
            </TextField>
            <Autocomplete
              options={contactOptions}
              value={selectedContact}
              getOptionSelected={(option, value) =>
                Number(option.id) === Number(value.id)
              }
              onOpen={() => setContactAutocompleteOpen(true)}
              onClose={() => setContactAutocompleteOpen(false)}
              getOptionLabel={option =>
                option
                  ? privacyCensorSettings.contactNames
                    ? "Contacto privado"
                    : `${option.name || "-"} - ${option.number || ""}`
                  : ""
              }
              renderOption={renderContactOption}
              onInputChange={(_, value, reason) => {
                if (reason === "input" || reason === "clear") {
                  setContactSearch(value);
                }
              }}
              onChange={(_, value) => {
                setSettings({ ...settings, contactId: value?.id || "" });
                setContactSearch("");
              }}
              disabled={!settings.senderCompanyId}
              renderInput={params => (
                <TextField
                  {...params}
                  label="Contacto"
                  variant="outlined"
                  margin="dense"
                />
              )}
            />
            <TextField
              label="Espera de"
              className={classes.delayField}
              helperText="Segundos"
              type="number"
              variant="outlined"
              margin="dense"
              value={settings.delaySeconds || 0}
              onChange={event =>
                setSettings({ ...settings, delaySeconds: event.target.value })
              }
            />
            <TextField
              className={classes.fullWidth}
              label="Mensaje para notificar"
              variant="outlined"
              margin="dense"
              multiline
              minRows={5}
              value={settings.message || ""}
              onChange={event =>
                setSettings({ ...settings, message: event.target.value })
              }
            />
            <div className={classes.fullWidth}>
              <input
                accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt,.zip,.rar,.webp"
                id="monitoring-notification-file"
                type="file"
                style={{ display: "none" }}
                onChange={event => {
                  setMediaFile(event.target.files?.[0] || null);
                  setClearMedia(false);
                }}
              />
              <label htmlFor="monitoring-notification-file">
                <Button variant="outlined" size="small" component="span">
                  Seleccionar archivo
                </Button>
              </label>
              <Typography variant="body2" component="span" style={{ marginLeft: 8 }}>
                {mediaFile?.name || settings.media?.originalName || "Sin archivo"}
              </Typography>
              {(mediaFile || settings.media) && (
                <Button
                  size="small"
                  style={{ marginLeft: 8 }}
                  onClick={() => {
                    setMediaFile(null);
                    setSettings({ ...settings, media: null });
                    setClearMedia(true);
                  }}
                >
                  Quitar
                </Button>
              )}
            </div>
          </div>
          <Typography variant="caption" className={classes.muted}>
            Variables: {"{{user.name}}"} {"{{user.email}}"} {"{{user.profile}}"}{" "}
            {"{{company.name}}"} {"{{loginAt}}"} {"{{ipAddress}}"} {"{{userAgent}}"}
          </Typography>
        </div>

        <div className={classes.section}>
          <div className={classes.sectionHeader}>
            <Typography variant="h6">Aviso de primera conexion vinculada</Typography>
            <Button
              color="primary"
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSaveFirstConnectionSettings}
            >
              Guardar
            </Button>
          </div>
          <div className={classes.settingsGrid}>
            <FormControlLabel
              control={
                <Switch
                  color="primary"
                  checked={Boolean(firstConnectionSettings.enabled)}
                  onChange={event =>
                    setFirstConnectionSettings({
                      ...firstConnectionSettings,
                      enabled: event.target.checked
                    })
                  }
                />
              }
              label="Enviar cuando una cuenta vincula su primera conexion"
            />
            <TextField
              select
              label="Cuenta destino"
              variant="outlined"
              margin="dense"
              value={
                firstConnectionSettings.destinationCompanyId ||
                NO_FIRST_CONNECTION_ACCOUNT
              }
              onChange={event =>
                setFirstConnectionSettings({
                  ...firstConnectionSettings,
                  destinationCompanyId:
                    event.target.value === NO_FIRST_CONNECTION_ACCOUNT
                      ? ""
                      : event.target.value,
                  destinationConnectionId: ""
                })
              }
            >
              <MenuItem value={NO_FIRST_CONNECTION_ACCOUNT}>Seleccionar</MenuItem>
              {notificationAccounts.map(account => (
                <MenuItem key={account.id} value={account.id}>
                  {account.name || `Cuenta ${account.id}`}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Conexion destino"
              variant="outlined"
              margin="dense"
              value={
                firstConnectionSettings.destinationConnectionId ||
                NO_FIRST_CONNECTION_DESTINATION
              }
              onChange={event =>
                setFirstConnectionSettings({
                  ...firstConnectionSettings,
                  destinationConnectionId:
                    event.target.value === NO_FIRST_CONNECTION_DESTINATION
                      ? ""
                      : event.target.value
                })
              }
              disabled={!firstConnectionSettings.destinationCompanyId}
              helperText="El numero de esta conexion recibira el mensaje."
            >
              <MenuItem value={NO_FIRST_CONNECTION_DESTINATION}>Seleccionar</MenuItem>
              {firstConnectionDestinations.map(connection => (
                <MenuItem key={connection.id} value={connection.id}>
                  {connection.name} ({connection.number || "sin numero"}) -{" "}
                  {connection.status || "sin estado"}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Espera de"
              className={classes.delayField}
              helperText="Segundos"
              type="number"
              variant="outlined"
              margin="dense"
              value={firstConnectionSettings.delaySeconds || 0}
              onChange={event =>
                setFirstConnectionSettings({
                  ...firstConnectionSettings,
                  delaySeconds: event.target.value
                })
              }
            />
            <TextField
              className={classes.fullWidth}
              label="Mensaje para enviar"
              variant="outlined"
              margin="dense"
              multiline
              minRows={5}
              value={firstConnectionSettings.message || ""}
              onChange={event =>
                setFirstConnectionSettings({
                  ...firstConnectionSettings,
                  message: event.target.value
                })
              }
            />
          </div>
          <Typography variant="caption" className={classes.muted}>
            Variables: {"{{user.name}}"} {"{{user.email}}"} {"{{company.name}}"}{" "}
            {"{{connection.name}}"} {"{{connection.number}}"} {"{{contactPhone}}"}{" "}
            {"{{connectedAt}}"}
          </Typography>
        </div>

        <div className={classes.section}>
          <div className={classes.sectionHeader}>
            <Typography variant="h6">Aviso de conexion desvinculada</Typography>
            <Button
              color="primary"
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSaveDisconnectedConnectionSettings}
            >
              Guardar
            </Button>
          </div>
          <div className={classes.settingsGrid}>
            <FormControlLabel
              control={
                <Switch
                  color="primary"
                  checked={Boolean(disconnectedConnectionSettings.enabled)}
                  onChange={event =>
                    setDisconnectedConnectionSettings({
                      ...disconnectedConnectionSettings,
                      enabled: event.target.checked
                    })
                  }
                />
              }
              label="Enviar cuando una conexion de WhatsApp se desvincula"
            />
            <TextField
              select
              label="Cuenta emisora"
              variant="outlined"
              margin="dense"
              value={
                disconnectedConnectionSettings.senderCompanyId ||
                NO_DISCONNECTED_SENDER_ACCOUNT
              }
              onChange={event =>
                setDisconnectedConnectionSettings({
                  ...disconnectedConnectionSettings,
                  senderCompanyId:
                    event.target.value === NO_DISCONNECTED_SENDER_ACCOUNT
                      ? ""
                      : event.target.value,
                  senderConnectionId: ""
                })
              }
            >
              <MenuItem value={NO_DISCONNECTED_SENDER_ACCOUNT}>Seleccionar</MenuItem>
              {notificationAccounts.map(account => (
                <MenuItem key={account.id} value={account.id}>
                  {account.name || `Cuenta ${account.id}`}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Conexion para enviar"
              variant="outlined"
              margin="dense"
              value={
                disconnectedConnectionSettings.senderConnectionId ||
                NO_DISCONNECTED_SENDER_CONNECTION
              }
              onChange={event =>
                setDisconnectedConnectionSettings({
                  ...disconnectedConnectionSettings,
                  senderConnectionId:
                    event.target.value === NO_DISCONNECTED_SENDER_CONNECTION
                      ? ""
                      : event.target.value
                })
              }
              disabled={!disconnectedConnectionSettings.senderCompanyId}
              helperText="Desde esta conexion se enviara el aviso al numero desvinculado."
            >
              <MenuItem value={NO_DISCONNECTED_SENDER_CONNECTION}>
                Seleccionar
              </MenuItem>
              {disconnectedSenderConnections.map(connection => (
                <MenuItem key={connection.id} value={connection.id}>
                  {connection.name} ({connection.number || "sin numero"}) -{" "}
                  {connection.status || "sin estado"}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Espera de"
              className={classes.delayField}
              helperText="Segundos"
              type="number"
              variant="outlined"
              margin="dense"
              value={disconnectedConnectionSettings.delaySeconds || 0}
              onChange={event =>
                setDisconnectedConnectionSettings({
                  ...disconnectedConnectionSettings,
                  delaySeconds: event.target.value
                })
              }
            />
            <TextField
              className={classes.fullWidth}
              label="Mensaje para enviar"
              variant="outlined"
              margin="dense"
              multiline
              minRows={4}
              value={disconnectedConnectionSettings.message || ""}
              onChange={event =>
                setDisconnectedConnectionSettings({
                  ...disconnectedConnectionSettings,
                  message: event.target.value
                })
              }
            />
          </div>
          <Typography variant="caption" className={classes.muted}>
            Variables: {"{{numero de whatsapp}}"} {"{{connection.name}}"}{" "}
            {"{{connection.number}}"} {"{{company.name}}"} {"{{disconnectedAt}}"}{" "}
            {"{{senderConnection.name}}"} {"{{senderConnection.number}}"}
          </Typography>
        </div>

        <div className={classes.section}>
          <div className={classes.sectionHeader}>
            <Typography variant="h6">Expiracion y eliminacion de cuentas</Typography>
            <Button
              color="primary"
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSaveExpirationAutomationSettings}
            >
              Guardar
            </Button>
          </div>
          <div className={classes.settingsGrid}>
            <FormControlLabel
              control={
                <Switch
                  color="primary"
                  checked={Boolean(expirationAutomationSettings.enabled)}
                  onChange={event =>
                    setExpirationAutomationSettings({
                      ...expirationAutomationSettings,
                      enabled: event.target.checked
                    })
                  }
                />
              }
              label="Enviar avisos antes de expirar"
            />
            <FormControlLabel
              control={
                <Switch
                  color="primary"
                  checked={Boolean(expirationAutomationSettings.deleteEnabled)}
                  onChange={event =>
                    setExpirationAutomationSettings({
                      ...expirationAutomationSettings,
                      deleteEnabled: event.target.checked
                    })
                  }
                />
              }
              label="Eliminar cuenta despues de expirar"
            />
            <TextField
              select
              label="Cuenta emisora"
              variant="outlined"
              margin="dense"
              value={
                expirationAutomationSettings.senderCompanyId ||
                NO_EXPIRATION_SENDER_ACCOUNT
              }
              onChange={event =>
                setExpirationAutomationSettings({
                  ...expirationAutomationSettings,
                  senderCompanyId:
                    event.target.value === NO_EXPIRATION_SENDER_ACCOUNT
                      ? ""
                      : event.target.value,
                  senderConnectionId: ""
                })
              }
            >
              <MenuItem value={NO_EXPIRATION_SENDER_ACCOUNT}>Seleccionar</MenuItem>
              {notificationAccounts.map(account => (
                <MenuItem key={account.id} value={account.id}>
                  {account.name || `Cuenta ${account.id}`}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Conexion para enviar"
              variant="outlined"
              margin="dense"
              value={
                expirationAutomationSettings.senderConnectionId ||
                NO_EXPIRATION_SENDER_CONNECTION
              }
              onChange={event =>
                setExpirationAutomationSettings({
                  ...expirationAutomationSettings,
                  senderConnectionId:
                    event.target.value === NO_EXPIRATION_SENDER_CONNECTION
                      ? ""
                      : event.target.value
                })
              }
              disabled={!expirationAutomationSettings.senderCompanyId}
            >
              <MenuItem value={NO_EXPIRATION_SENDER_CONNECTION}>
                Seleccionar
              </MenuItem>
              {expirationSenderConnections.map(connection => (
                <MenuItem key={connection.id} value={connection.id}>
                  {connection.name} ({connection.number || "sin numero"}) -{" "}
                  {connection.status || "sin estado"}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Eliminar despues de"
              helperText="Dias despues de expirar"
              type="number"
              variant="outlined"
              margin="dense"
              value={expirationAutomationSettings.deleteAfterDays || 0}
              onChange={event =>
                setExpirationAutomationSettings({
                  ...expirationAutomationSettings,
                  deleteAfterDays: event.target.value
                })
              }
            />
            <TextField
              className={classes.fullWidth}
              label="Mensaje 3 dias antes"
              variant="outlined"
              margin="dense"
              multiline
              minRows={4}
              value={expirationAutomationSettings.day3Message || ""}
              onChange={event =>
                setExpirationAutomationSettings({
                  ...expirationAutomationSettings,
                  day3Message: event.target.value
                })
              }
            />
            <TextField
              className={classes.fullWidth}
              label="Mensaje 2 dias antes"
              variant="outlined"
              margin="dense"
              multiline
              minRows={4}
              value={expirationAutomationSettings.day2Message || ""}
              onChange={event =>
                setExpirationAutomationSettings({
                  ...expirationAutomationSettings,
                  day2Message: event.target.value
                })
              }
            />
            <TextField
              className={classes.fullWidth}
              label="Mensaje 1 dia antes"
              variant="outlined"
              margin="dense"
              multiline
              minRows={4}
              value={expirationAutomationSettings.day1Message || ""}
              onChange={event =>
                setExpirationAutomationSettings({
                  ...expirationAutomationSettings,
                  day1Message: event.target.value
                })
              }
            />
          </div>
          <Typography variant="caption" className={classes.muted}>
            Variables: {"{{user.name}}"} {"{{user.email}}"} {"{{company.name}}"}{" "}
            {"{{expiresAt}}"} {"{{daysBefore}}"} {"{{deleteAfterDays}}"}{" "}
            {"{{contactPhone}}"}
          </Typography>
        </div>

        <div className={classes.section}>
          <Typography variant="h6">Usuarios online</Typography>
          <div className={classes.tableWrap}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Usuario</TableCell>
                  <TableCell>Empresa</TableCell>
                  <TableCell>Perfil</TableCell>
                  <TableCell>Conectado desde</TableCell>
                  <TableCell>Pestanas</TableCell>
                  <TableCell>Sockets</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {onlineUsers.map(user => (
                  <TableRow key={`${user.companyId}-${user.userId}`}>
                    <TableCell>
                      <Typography variant="body2">{user.name}</Typography>
                      <Typography variant="caption" className={classes.muted}>
                        {user.email}
                      </Typography>
                    </TableCell>
                    <TableCell>{user.companyName || user.companyId}</TableCell>
                    <TableCell>{formatProfile(user.profile)}</TableCell>
                    <TableCell>{formatDate(user.connectedAt)}</TableCell>
                    <TableCell>
                      <Chip size="small" label={user.tabs || user.sockets} />
                    </TableCell>
                    <TableCell>{user.sockets}</TableCell>
                  </TableRow>
                ))}
                {!onlineUsers.length && (
                  <TableRow>
                    <TableCell colSpan={6}>No hay usuarios online.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        <div className={classes.section}>
          <Typography variant="h6">Logs de sesiones</Typography>
          <div className={classes.tableWrap}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Usuario</TableCell>
                  <TableCell>Empresa</TableCell>
                  <TableCell>Inicio</TableCell>
                  <TableCell>Cierre</TableCell>
                  <TableCell>Tiempo</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>IP</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map(log => (
                  <TableRow key={log.id}>
                    <TableCell>{log.user?.name || log.userId}</TableCell>
                    <TableCell>{log.company?.name || log.companyId}</TableCell>
                    <TableCell>{formatDate(log.loginAt)}</TableCell>
                    <TableCell>{formatDate(log.logoutAt)}</TableCell>
                    <TableCell>{formatDuration(log.durationSeconds)}</TableCell>
                    <TableCell>{log.status}</TableCell>
                    <TableCell>{log.ipAddress || "-"}</TableCell>
                  </TableRow>
                ))}
                {!logs.length && (
                  <TableRow>
                    <TableCell colSpan={7}>No hay logs registrados.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          <TablePagination
            component="div"
            count={count}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={event => {
              setRowsPerPage(Number(event.target.value));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 30, 50]}
          />
        </div>
      </Paper>
    </MainContainer>
  );
};

export default Monitoring;
